// material-format.ts — material format overflow (joins) and technical margins.
//
// A material format is stored as "WIDTH × LENGTH" in mm. Does a quote piece (unit dimensions, never
// the cumul) fit in that format once technical margins are deducted? If not, the line is delivered
// in several joined pieces. Pure and UI-independent: the engine decides, the UI displays.
//
// Two geometries depending on the material nature: flexible materials are rolls (usable width =
// web width minus twice the web margin, usable length minus twice the reel margin), rigid ones are
// sheets (each usable dimension minus twice the sheet margin). Rotation is always allowed.
//
// Margins are editable supervisor parameters (`app_settings`) injected into the engine, never hardcoded:
// there is no default value here, every caller passes the margins it was given.

import { engineMessage } from "../core/messages";
import type { MaterialNature } from "../pricing/pa-material";

/** Technical margins in mm: roll web, sheet sides, reel ends. */
export interface TechnicalMargins {
  /** Roll: margin on each side of the web width. */
  webMargin: number;
  /** Sheet: margin on all four sides. */
  sheetMargin: number;
  /** Roll: margin at each end of the length. */
  reelMargin: number;
  /** Production summary: inter-piece spacing of the imposition estimate. Never used by the overflow test. */
  pieceGap: number;
}

/** All-zero margins, so the usable format equals the raw format. Inter-piece spacing is zeroed too:
 *  raw material without cutting needs no camera marks or cutting margin, so imposition is edge to edge. */
export const ZERO_MARGINS: TechnicalMargins = { webMargin: 0, sheetMargin: 0, reelMargin: 0, pieceGap: 0 };

/**
 * Should technical margins be neutralized for this line, making the usable format the raw one?
 * Requires both no print (IMP-1419) and raw material without cutting (CUT-1511), for flexible and
 * rigid alike. Any other combination keeps the margins.
 * @param print The line's print mode code.
 * @param cut The line's cut code.
 * @returns True when the margins are neutralized.
 */
export function neutralizedMargins(print?: string | null, cut?: string | null): boolean {
  return print === "IMP-1419" && cut === "CUT-1511";
}

/** Verdict of a line's overflow test. */
export interface OverflowVerdict {
  /** A test actually ran (format known and dimensions filled). False means no format, so no test. */
  tested: boolean;
  /** The piece does not fit in the selected format, margins included, so a join is required. */
  overflows: boolean;
}

/**
 * Parses a format string like "1370 × 50000" into [1370, 50000] in mm, splitting on × or x.
 * Returns null when the format is absent or unreadable.
 * @param format The format string.
 * @returns The two dimensions in mm, or null.
 */
export function parseFormat(format: string | null | undefined): [number, number] | null {
  if (!format) return null;
  const parts = String(format)
    .split(/[×x]/i)
    .map((s) => Number(s.replace(/\s/g, "")));
  if (parts.length !== 2) return null;
  const a = parts[0]!;
  const b = parts[1]!;
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return [a, b];
}

/**
 * Does the piece (unit L × H in mm) fit in the material's `format` for the given nature, margins
 * deducted? Rotation is allowed. Missing format or dimensions means no test at all.
 * @param L The piece length in mm.
 * @param H The piece height in mm.
 * @param format The material format string.
 * @param nature The material nature (flexible or rigid).
 * @param margins The technical margins.
 * @returns The overflow verdict.
 */
export function checkOverflow(
  L: number,
  H: number,
  format: string | null | undefined,
  nature: MaterialNature,
  margins: TechnicalMargins,
): OverflowVerdict {
  const dims = parseFormat(format);
  if (!dims || !(L > 0) || !(H > 0)) return { tested: false, overflows: false };
  const [width, length] = dims;
  const isRoll = nature === "flexible";
  // Roll: width uses the web margin, length the reel margin. Sheet: both use the sheet margin.
  const usableWidth = width - 2 * (isRoll ? margins.webMargin : margins.sheetMargin);
  const usableLength = length - 2 * (isRoll ? margins.reelMargin : margins.sheetMargin);
  const fits =
    (L <= usableWidth && H <= usableLength) || (H <= usableWidth && L <= usableLength);
  return { tested: true, overflows: !fits };
}

/**
 * Whether a line is delivered in joined pieces. The « raccord » box decides; left untouched, it follows the overflow
 * (ticked when the piece does not fit in the material format), and it can be unticked even then.
 * @param manual The box as set by hand (undefined while nobody touched it since the last size change).
 * @param overflows Whether the piece does not fit in the retained format.
 * @returns Whether the line is spliced.
 */
export function effectiveSplice(manual: boolean | undefined, overflows: boolean): boolean {
  return manual ?? overflows;
}

/** Fixed client-facing notice when a line is delivered in joined pieces (carried by export and Sellsy). */
export const SPLICE_NOTICE = engineMessage("engineSpliceNotice");

// The two production notes below address the WORKSHOP, unlike SPLICE_NOTICE which goes to the
// client. Two audiences, two texts.

/** Production note when the selected format is not the material's default: a width must be ordered. */
export const SUPPLY_NOTE = engineMessage("engineSupplyNote");
/** Production note when the piece does not fit the selected format, margins included. */
export const SPLICE_NOTE = engineMessage("engineSpliceNote");

/**
 * Picks the material format from the piece dimensions. Prefers the material's default when it fits,
 * otherwise the smallest format by surface that fits, to order as little material as possible.
 * When nothing fits, the default is kept on purpose since the piece needs joining anyway.
 *
 * This is only a proposal, applied to lines whose format was not set by hand. Returns `null` when
 * there is nothing to change (empty list, missing dimensions, or the default already fits).
 * @param formats The material's available formats.
 * @param defaultFormat The material's default format.
 * @param L The piece length in mm.
 * @param H The piece height in mm.
 * @param nature The material nature (flexible or rigid).
 * @param margins The technical margins.
 * @returns The proposed format, or null when nothing needs to change.
 */
export function formatAuto(
  formats: readonly string[],
  defaultFormat: string | null | undefined,
  L: number,
  H: number,
  nature: MaterialNature,
  margins: TechnicalMargins,
): string | null {
  if (!(L > 0) || !(H > 0) || formats.length === 0) return null;
  /**
   * Does the piece fit in the given format, margins deducted?
   * @param f The format string.
   * @returns True when an overflow test ran and the piece fits.
   */
  const fits = (f: string) => {
    const v = checkOverflow(L, H, f, nature, margins);
    return v.tested && !v.overflows;
  };
  if (defaultFormat && fits(defaultFormat)) return null; // the standard fits: nothing to move
  /**
   * Surface of a format, infinite when unreadable.
   * @param f The format string.
   * @returns The surface in mm².
   */
  const surface = (f: string) => {
    const d = parseFormat(f);
    return d ? d[0] * d[1] : Number.POSITIVE_INFINITY;
  };
  const candidates = formats.filter(fits).sort((a, b) => surface(a) - surface(b));
  return candidates[0] ?? null; // smallest that fits, else keep the default
}

// Piece imposition, shared by compute (UPZ material purchase) and the production summary.

/**
 * Imposition of one rigid piece: pieces per sheet, best of the two orientations.
 * @param usableL The usable sheet length in mm.
 * @param usableH The usable sheet height in mm.
 * @param L The piece length in mm.
 * @param H The piece height in mm.
 * @param gap The inter-piece spacing in mm.
 * @returns The number of pieces per sheet.
 */
export function piecesPerSheet(usableL: number, usableH: number, L: number, H: number, gap: number): number {
  const o1 = Math.floor(usableL / (L + gap)) * Math.floor(usableH / (H + gap));
  const o2 = Math.floor(usableL / (H + gap)) * Math.floor(usableH / (L + gap));
  return Math.max(o1, o2);
}

/**
 * Linear metres of one flexible piece: the orientation minimizing length wins; null means it does not fit.
 * @param usableWebWidth The usable web width in mm.
 * @param usableLength The usable reel length in mm.
 * @param L The piece length in mm.
 * @param H The piece height in mm.
 * @param qty The piece quantity.
 * @param gap The inter-piece spacing in mm.
 * @returns The consumed length in mm, or null when the piece does not fit.
 */
export function linearLengthPerPiece(usableWebWidth: number, usableLength: number, L: number, H: number, qty: number, gap: number): number | null {
  const orientations: number[] = [];
  for (const [l, h] of [[L, H], [H, L]] as const) {
    const perRow = Math.floor(usableWebWidth / (l + gap));
    if (perRow < 1) continue;
    if (h > usableLength) continue; // piece longer than the usable reel
    orientations.push(Math.ceil(qty / perRow) * (h + gap));
  }
  if (!orientations.length) return null;
  return Math.min(...orientations);
}
